import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import IconButton from '@mui/material/IconButton';
import { useState, ChangeEvent, useEffect } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import { useNavigate } from 'react-router-dom';
import { ICampaign } from '../../interfaces/campaign';
import { useCampaignStatusColors } from '../../hooks/useCampaignStatusColors';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';
import { useDebounce } from 'use-debounce';

export default function CampaignListTable() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const [loading, setLoading] = useState(false);
	const [fetchedData, setFetchedData] = useState<ICampaign[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleViewCampaign = (campaignId: string, action: string) => () => {
		navigate(`/campaigns/view/${campaignId}?action=${action}`);
	};

	const handleDeleteCampaign = (campaignId: string) => () => {
		axios<IResponse<boolean>>({
			url: `/campaigns/${parseInt(campaignId)}`,
			method: 'DELETE',
		}).then(res => {
			if (res.data.success) {
				fetchCampaigns();
			}
		});
	};

	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const fetchCampaigns = () => {
		setLoading(true);
		axios<IResponse<ICampaign[]>>({
			url: '/campaigns',
			params: {
				page: page,
				limit: pageSize,
				search: search,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleCreateCampaign = () => {
		navigate('/campaigns/create');
	};

	const properties: (keyof ICampaign)[] = ['title', 'content_type', 'media', 'status'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<ICampaign>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{ field: 'title', flex: 1, headerName: t('campaign_name'), headerAlign: 'center', align: 'center', headerClassName: headerClassNames },
		{
			field: 'content_type',
			headerName: t('content_type'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return t(params.row.content_type!);
			},
		},
		{
			field: 'media',
			headerName: t('release-platform'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const formatted = params.row.media === 'sms' ? 'sms_panel' : params.row.media;
				return t(formatted!);
			},
		},
		{
			field: 'created_at',
			headerName: t('creation_date'),
			flex: 1,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'release_time',
			headerName: t('hour'),
			width: 100,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
		},
		{
			field: 'status',
			headerName: t('status'),
			flex: 1,
			headerClassName: `${headerClassNames}`,
			headerAlign: 'center',
			renderCell: params => {
				const bgColorClass = useCampaignStatusColors(params.value);
				return <div className={`mx-auto mt-4 flex w-[max-content] rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{t(params.row.status!)}</div>;
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 160,
			headerAlign: 'center',
			headerClassName: headerClassNames,
			align: 'right',
			renderCell: params => {
				const isDone = params.row.status === 'completed';
				const editableData = ['pending', 'payment_pend', 'rejected'].includes(params?.row?.status ?? '');
				return (
					<>
						{editableData && (
							<IconButton onClick={handleViewCampaign(params.row.id, 'edit')} disabled={isDone}>
								<BorderColorOutlinedIcon className={isDone ? 'text-text-disabled' : 'text-text-secondary'} />
							</IconButton>
						)}
						<IconButton onClick={handleViewCampaign(params.row.id, 'show')}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleDeleteCampaign(params.row.id)} disabled={isDone}>
							<DeleteOutlineOutlinedIcon className={isDone ? 'text-text-disabled' : 'text-text-secondary'} />
						</IconButton>
					</>
				);
			},
		},
	];

	useEffect(() => {
		fetchCampaigns();
	}, [page, pageSize, search]);

	return <BaranDataGrid<ICampaign> pagination={pagination} loading={loading} rows={filteredRows} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={handleCreateCampaign} actionButtonLable={t('create_new_campaign')} searchableFields={properties} />;
}
